/**
 * パスワードフォーム共通フィールド
 *
 * パスワード変更ダイアログ・初回パスワード設定ページで共通利用する。
 * フォーム入力欄・条件チェック・バリデーション・API呼び出しを担当し、
 * レイアウト（ダイアログ/ページ）は呼び出し側が決める。
 */

import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";
import { useChangePasswordMutation } from "../../../shared/api/endpoints/authApi";
import { Input, Button } from "../../../components/atoms";
import { validatePassword, PASSWORD_CONDITIONS } from "../utils/passwordValidation";
import { useAuth } from "../../../contexts/AuthContext";

/** エラーレスポンスから表示用メッセージを抽出 */
function extractErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null && "data" in err) {
    const data = (err as { data: unknown }).data;
    if (typeof data === "object" && data !== null && "detail" in data) {
      const detail = (data as { detail: unknown }).detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail.map((d: { msg?: string }) => d.msg ?? "").filter(Boolean).join(", ");
      }
    }
  }
  return "パスワード変更に失敗しました";
}

/** パスワード条件のリアルタイム表示 */
export const PasswordConditionList: FC<{ password: string; loginId: string }> = ({ password, loginId }) => {
  const results = useMemo(
    () => PASSWORD_CONDITIONS.map((c) => ({ label: c.label, met: c.test(password, loginId) })),
    [password, loginId],
  );

  return (
    <div className="rounded-lg bg-brand-50 border border-brand-100 p-3">
      <p className="text-sm font-medium text-gray-700 mb-2">パスワード条件</p>
      <ul className="space-y-1.5">
        {results.map((r) => (
          <li key={r.label} className="flex items-center gap-2 text-sm">
            {r.met ? (
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#B99140" />
                <path d="M6 10l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="#F87171" strokeWidth="2" fill="none" />
                <path d="M7 7l6 6M13 7l-6 6" stroke="#F87171" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            <span className={r.met ? "text-brand-500" : "text-error-500"}>{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export interface PasswordFormFieldsProps {
  /** 送信ボタンのテキスト */
  submitLabel: string;
  /** 現在のパスワードのプレースホルダー */
  currentPasswordPlaceholder?: string;
  /** フォーム上部に表示する追加コンテンツ（案内バナー等） */
  banner?: ReactNode;
  /** 変更成功時のコールバック */
  onSuccess: () => void;
}

export const PasswordFormFields: FC<PasswordFormFieldsProps> = ({
  submitLabel,
  currentPasswordPlaceholder = "現在のパスワードを入力",
  banner,
  onSuccess,
}) => {
  const { user } = useAuth();
  const loginId = user?.code ?? "";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  /** 現在のパスワードと新しいパスワードが同じか */
  const isSameAsCurrent = currentPassword !== "" && newPassword !== "" && currentPassword === newPassword;

  /** すべての条件を満たし、全フィールドが入力済みかどうか */
  const isFormValid = useMemo(() => {
    if (!currentPassword || !newPassword || !confirmPassword) return false;
    if (newPassword !== confirmPassword) return false;
    if (isSameAsCurrent) return false;
    return validatePassword(newPassword, loginId) === null;
  }, [currentPassword, newPassword, confirmPassword, loginId, isSameAsCurrent]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword) {
      setError("現在のパスワードを入力してください");
      return;
    }
    const validationError = validatePassword(newPassword, loginId);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("新しいパスワードが一致しません");
      return;
    }

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      }).unwrap();
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onSuccess();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [currentPassword, newPassword, confirmPassword, loginId, changePassword, onSuccess]);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {banner}

      <Input
        label="現在のパスワード"
        placeholder={currentPasswordPlaceholder}
        isPassword
        showPasswordToggle
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        disabled={isLoading}
        autoComplete="current-password"
      />

      <Input
        label="新しいパスワード"
        placeholder="新しいパスワードを入力"
        isPassword
        showPasswordToggle
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={isLoading}
        autoComplete="new-password"
        error={isSameAsCurrent ? "現在のパスワードと異なるパスワードを設定してください" : undefined}
      />

      <PasswordConditionList password={newPassword} loginId={loginId} />

      <Input
        label="新しいパスワードの確認"
        placeholder="もう一度入力してください"
        isPassword
        showPasswordToggle
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={isLoading}
        autoComplete="new-password"
        error={confirmPassword && newPassword !== confirmPassword ? "パスワードが一致しません" : undefined}
      />

      {error && (
        <div className="rounded-lg bg-error-50 border border-error-200 p-3">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      )}

      <div className="flex justify-center">
        <Button
          type="submit"
          variant="primary"
          size="md"
          isLoading={isLoading}
          disabled={!isFormValid || isLoading}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
